import { useState } from 'react';
import {
  Button, Col, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Table,
  Upload, message,
} from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import moment from 'moment/moment';
import {
  arrayOf, bool, func, object,
} from 'prop-types';
import FileDownload from '@/components/FileDownload/FileDownload';

const EDITABLE_FIELDS = [
  'guaranteeObject',
  'guaranteeSort',
  'guaranteeIou',
  'guaranteeType',
  'guaranteeStart',
  'guaranteeEnd',
  'collaboratingFinancial',
  'guaranteeLiabilityQuota',
  'rate',
  'newAmount',
  'guaranteeBonusLoanFileVos',
];

export default function GuaranteeInfoTable({
  data,
  editable,
  showReason,
  uploadProps,
  onSave,
  onDelete,
  onProveFileUpload,
  onProveFileRemove,
  onEditingChange,
}) {
  const [editingKey, setEditingKey] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [editingErrors, setEditingErrors] = useState({});
  const [fileUploading, setFileUploading] = useState(false);
  const [searchValues, setSearchValues] = useState({ guaranteeObject: '' });
  const [searchFilters, setSearchFilters] = useState({});

  const startEdit = (record) => {
    setEditingKey(record.id);
    setEditingValues({ ...record });
    setEditingErrors({});
    setFileUploading(false);
    onEditingChange(true);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingValues({});
    setEditingErrors({});
    setFileUploading(false);
    onEditingChange(false);
  };

  const changeEditingValue = (field, value) => {
    setEditingValues((previousValues) => ({
      ...previousValues,
      [field]: value,
    }));
    setEditingErrors((previousErrors) => ({
      ...previousErrors,
      [field]: false,
    }));
  };

  const saveEdit = () => {
    if (fileUploading) {
      message.warning('请等待证明材料上传完成');
      return;
    }
    const errors = {};
    EDITABLE_FIELDS.forEach((field) => {
      const value = editingValues[field];
      if (value === undefined || value === null || String(value).trim() === '') {
        errors[field] = true;
      }
    });

    if (Object.keys(errors).length > 0) {
      setEditingErrors(errors);
      message.error('请填写完整的担保信息');
      return;
    }

    onSave({
      ...editingValues,
      duration: `${editingValues.guaranteeStart}~${editingValues.guaranteeEnd}`,
    });
    cancelEdit();
  };

  const renderEditingItem = (field, label, children) => (
    <Form.Item
      label={label}
      required
      validateStatus={editingErrors[field] ? 'error' : ''}
      help={editingErrors[field] ? '必填' : null}
      style={{ marginBottom: 16, width: '100%' }}
    >
      {children}
    </Form.Item>
  );

  const renderEditingInput = (field, label, placeholder) => renderEditingItem(
    field,
    label,
    (
      <Input
        value={editingValues[field]}
        placeholder={placeholder}
        style={{ width: '100%' }}
        onChange={(event) => changeEditingValue(field, event.target.value)}
      />
    ),
  );

  const renderEditingNumber = (field, label, placeholder) => renderEditingItem(
    field,
    label,
    (
      <InputNumber
        value={editingValues[field]}
        placeholder={placeholder}
        style={{ width: '100%' }}
        onChange={(value) => changeEditingValue(field, value)}
      />
    ),
  );

  const renderEditingSelect = (field, label, options, placeholder) => renderEditingItem(
    field,
    label,
    (
      <Select
        value={editingValues[field]}
        options={options.map((value) => ({ label: value, value }))}
        placeholder={placeholder}
        style={{ width: '100%' }}
        onChange={(value) => changeEditingValue(field, value)}
      />
    ),
  );

  const renderEditingDateRange = () => {
    const { guaranteeStart, guaranteeEnd } = editingValues;
    const value = guaranteeStart && guaranteeEnd
      ? [moment(guaranteeStart, 'YYYY-MM-DD'), moment(guaranteeEnd, 'YYYY-MM-DD')]
      : null;
    const hasError = editingErrors.guaranteeStart || editingErrors.guaranteeEnd;

    return (
      <Form.Item
        label="担保起止时间"
        required
        validateStatus={hasError ? 'error' : ''}
        help={hasError ? '必填' : null}
        style={{ marginBottom: 16, width: '100%' }}
      >
        <DatePicker.RangePicker
          value={value}
          format="YYYY-MM-DD"
          placeholder={['开始日期', '结束日期']}
          style={{ width: '100%' }}
          onChange={(_, dateStrings) => {
            changeEditingValue('guaranteeStart', dateStrings[0]);
            changeEditingValue('guaranteeEnd', dateStrings[1]);
          }}
        />
      </Form.Item>
    );
  };

  const handleEditingFileUpload = ({ file }) => {
    setFileUploading(file.status === 'uploading');
    if (file.status === 'uploading') {
      changeEditingValue('guaranteeBonusLoanFileVos', file);
    } else if (file.status === 'done') {
      changeEditingValue('guaranteeBonusLoanFileVos', file);
      message.success('证明材料上传成功!');
    } else if (file.status === 'error') {
      changeEditingValue('guaranteeBonusLoanFileVos', undefined);
      message.error('上传失败');
    }
  };

  const editingFile = editingValues.guaranteeBonusLoanFileVos;
  const editingFileList = editingFile ? [{
    ...editingFile,
    uid: editingFile.uid ?? '-1',
    name: editingFile.name ?? editingFile.fileName,
    status: editingFile.status ?? 'done',
    url: editingFile.url ?? editingFile.fileUrl,
  }] : [];

  const columns = [
    {
      title: '担保对象',
      dataIndex: 'guaranteeObject',
      width: '10%',
    },
    {
      title: '分类',
      dataIndex: 'guaranteeSort',
      width: '8%',
    },
    {
      title: '担保合同号',
      dataIndex: 'guaranteeIou',
      width: '8%',
    },
    {
      title: '担保类别',
      dataIndex: 'guaranteeType',
      width: '6%',
    },
    {
      title: '担保起止时间',
      dataIndex: 'guaranteeStart',
      width: '12%',
      render: (text, record) => `${text}~${record.guaranteeEnd}`,
    },
    {
      title: '协作金融机构名称',
      dataIndex: 'collaboratingFinancial',
      width: '10%',
    },
    {
      title: '担保责任额(万元)',
      dataIndex: 'guaranteeLiabilityQuota',
      width: '7%',
    },
    {
      title: '担保费率(%)',
      dataIndex: 'rate',
      width: '6%',
    },
    {
      title: '当年新发生额(万元)',
      dataIndex: 'newAmount',
      width: '7%',
    },
    {
      title: '证明材料',
      dataIndex: 'guaranteeBonusLoanFileVos',
      width: 120,
      render: (file, record) => {
        const displayName = file ? file.name ?? file.fileName : '';
        if (!editable) {
          if (file?.fileUrl) {
            return (
              <FileDownload
                appendixName={file.fileName ?? file.name}
                fileUrl={file.fileUrl}
              />
            );
          }
          return displayName || '-';
        }
        return file ? (
          <div
            style={{
              display: 'flex', alignItems: 'center', width: '100%', minWidth: 0,
            }}
          >
            <Popconfirm title="确定要删除吗？" onConfirm={() => onProveFileRemove(record)}>
              <Button
                type="link"
                size="small"
                title={displayName}
                icon={<DeleteOutlined />}
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  height: 'auto',
                  padding: '0 4px',
                  lineHeight: '20px',
                  textAlign: 'left',
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                }}
              >
                {displayName}
              </Button>
            </Popconfirm>
            <Upload
              {...uploadProps}
              maxCount={1}
              showUploadList={false}
              style={{ flex: '0 0 auto' }}
              onChange={(fileVo) => onProveFileUpload(fileVo, record)}
            >
              <Button
                type="link"
                size="small"
                title="重新上传"
                aria-label="重新上传证明材料"
                icon={<UploadOutlined />}
              />
            </Upload>
          </div>
        ) : (
          <Upload
            {...uploadProps}
            onChange={(fileVo) => onProveFileUpload(fileVo, record)}
            maxCount={1}
          >
            <Button size="small" icon={<UploadOutlined />}>上传</Button>
          </Upload>
        );
      },
    },
  ];

  if (showReason) {
    columns.push({
      title: '原因',
      dataIndex: 'webReason',
    });
  }

  if (editable) {
    columns.push({
      title: '操作',
      dataIndex: 'operation',
      width: 110,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            disabled={editingKey !== null}
            onClick={() => startEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm title="确定要删除这条担保信息吗？" onConfirm={() => onDelete(record)}>
            <Button type="link" size="small" danger disabled={editingKey !== null}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  const filteredData = data.filter((item) => {
    const guaranteeObject = String(item.guaranteeObject ?? '').toLowerCase();
    const objectKeyword = String(searchFilters.guaranteeObject ?? '').trim().toLowerCase();
    const objectMatched = !objectKeyword || guaranteeObject.includes(objectKeyword);
    const typeMatched = !searchFilters.guaranteeType
      || item.guaranteeType === searchFilters.guaranteeType;
    const isFileUploaded = Boolean(item.guaranteeBonusLoanFileVos);
    const fileMatched = searchFilters.fileUploaded === undefined
      || isFileUploaded === searchFilters.fileUploaded;
    return objectMatched && typeMatched && fileMatched;
  });

  const resetSearch = () => {
    setSearchValues({ guaranteeObject: '' });
    setSearchFilters({});
  };

  return (
    <>
      <Space wrap size="small" style={{ marginBottom: 12 }}>
        <Input
          allowClear
          size="small"
          value={searchValues.guaranteeObject}
          placeholder="请输入担保对象"
          style={{ width: 180 }}
          disabled={editingKey !== null}
          onChange={(event) => setSearchValues({
            ...searchValues,
            guaranteeObject: event.target.value,
          })}
          onPressEnter={(event) => {
            event.preventDefault();
            setSearchFilters({ ...searchValues });
          }}
        />
        <Select
          allowClear
          size="small"
          value={searchValues.guaranteeType}
          placeholder="请选择担保类别"
          style={{ width: 160 }}
          disabled={editingKey !== null}
          options={['担保', '联保', '再担保'].map((value) => ({ label: value, value }))}
          onChange={(value) => setSearchValues({ ...searchValues, guaranteeType: value })}
        />
        <Select
          allowClear
          size="small"
          value={searchValues.fileUploaded}
          placeholder="证明材料上传状态"
          style={{ width: 180 }}
          disabled={editingKey !== null}
          options={[
            { label: '已上传证明材料', value: true },
            { label: '未上传证明材料', value: false },
          ]}
          onChange={(value) => setSearchValues({ ...searchValues, fileUploaded: value })}
        />
        <Button
          type="primary"
          size="small"
          disabled={editingKey !== null}
          onClick={() => setSearchFilters({ ...searchValues })}
        >
          查询
        </Button>
        <Button size="small" disabled={editingKey !== null} onClick={resetSearch}>重置</Button>
      </Space>
      <h4 style={{ marginBottom: 8 }}>
        <span style={{ color: 'red', marginRight: 4 }}>*</span>
        请点击【模板下载】下载担保业务信息模板，严格按照模板字段填报担保业务信息，并核对确认填报内容准确无误。点击【上传】担保信息文件时多次上传新文件将自动覆盖历史文件，系统以最后一次上传的文件数据为准。

      </h4>
      <h4 style={{ marginBottom: 8 }}>
        <span style={{ color: 'red', marginRight: 4 }}>*</span>
        上传担保信息【证明材料】的文件请把每笔担保贷款的合同、到账凭证、保费收取凭证扫描在一起。
      </h4>
      <Table
        size="small"
        dataSource={filteredData}
        columns={columns}
        rowKey="id"
        tableLayout="fixed"
        pagination={{
          size: 'small',
          showSizeChanger: true,
          showQuickJumper: true,
        }}
      />
      <Modal
        title="编辑担保信息"
        visible={editingKey !== null}
        width={960}
        okText="保存"
        cancelText="取消"
        confirmLoading={fileUploading}
        maskClosable={false}
        destroyOnClose
        onOk={saveEdit}
        onCancel={cancelEdit}
      >
        <Form
          layout="horizontal"
          labelCol={{ flex: '150px' }}
          wrapperCol={{ flex: 1 }}
          labelAlign="right"
          colon={false}
        >
          <Row gutter={20}>
            <Col span={12}>
              {renderEditingInput('guaranteeObject', '担保对象', '请输入担保对象')}
            </Col>
            <Col span={12}>
              {renderEditingSelect(
                'guaranteeSort',
                '分类',
                ['粮食生产经营', '现代农业发展信贷'],
                '请选择分类',
              )}
            </Col>
            <Col span={12}>
              {renderEditingInput('guaranteeIou', '担保合同号', '请输入担保合同号')}
            </Col>
            <Col span={12}>
              {renderEditingSelect(
                'guaranteeType',
                '担保类别',
                ['担保', '联保', '再担保'],
                '请选择担保类别',
              )}
            </Col>
            <Col span={12}>{renderEditingDateRange()}</Col>
            <Col span={12}>
              {renderEditingInput(
                'collaboratingFinancial',
                '协作金融机构名称',
                '请输入协作金融机构名称',
              )}
            </Col>
            <Col span={12}>
              {renderEditingNumber(
                'guaranteeLiabilityQuota',
                '担保责任额（万元）',
                '请输入担保责任额',
              )}
            </Col>
            <Col span={12}>
              {renderEditingNumber('rate', '担保费率（%）', '请输入担保费率')}
            </Col>
            <Col span={12}>
              {renderEditingNumber('newAmount', '当年新发生额（万元）', '请输入当年新发生额')}
            </Col>
            <Col span={24}>
              <Form.Item
                label="证明材料"
                required
                validateStatus={editingErrors.guaranteeBonusLoanFileVos ? 'error' : ''}
                help={editingErrors.guaranteeBonusLoanFileVos ? '请上传证明材料' : null}
                style={{ marginBottom: 0 }}
              >
                <Upload
                  {...uploadProps}
                  fileList={editingFileList}
                  maxCount={1}
                  showUploadList={{ showRemoveIcon: !fileUploading }}
                  onChange={handleEditingFileUpload}
                  onRemove={() => {
                    changeEditingValue('guaranteeBonusLoanFileVos', undefined);
                    return true;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={fileUploading}>
                    {editingFile ? '重新上传' : '上传'}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

GuaranteeInfoTable.propTypes = {
  data: arrayOf(object),
  editable: bool,
  showReason: bool,
  uploadProps: object,
  onSave: func,
  onDelete: func,
  onProveFileUpload: func,
  onProveFileRemove: func,
  onEditingChange: func,
};

GuaranteeInfoTable.defaultProps = {
  data: [],
  editable: true,
  showReason: false,
  uploadProps: {},
  onSave: () => {},
  onDelete: () => {},
  onProveFileUpload: () => {},
  onProveFileRemove: () => {},
  onEditingChange: () => {},
};
